import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { PlusCircle as PlusCircleIcon } from 'react-feather'
import { Box, Button, FormHelperText } from '@material-ui/core'

import { ConfirmationDialog, Label, MenuButton } from 'components'
import { Table, TableBody, TableCell, TableRow } from 'components/Table'

import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import helpers from 'helpers'
import constants from 'constants/index'
import { getBaseURL } from 'service/env'

import useStyles from './styles'

const EvidenceDocumentsTable = ({
  controlName = 'evidences',
  attachments = null,
}) => {
  const [documentToRemove, setDocumentToRemove] = useState(null)

  const { register, watch, setValue, errors } = useFormContext()
  const { isShow } = useThirdPartyManagementStepper()
  const inputRef = useRef(null)
  const classes = useStyles()

  const { EVIDENCE_FILE_TYPES } = constants.planAction

  const watchedValue = watch(controlName)
  const documents = helpers.file.toArray(watchedValue ?? attachments)

  const isFileDocument = (document) => document instanceof File

  const documentName = (document) =>
    isFileDocument(document)
      ? document.name
      : document?.name || helpers.functions.documentName(document?.url ?? '')

  const handleAddClick = () => inputRef.current?.click()

  const handleFilesSelected = (event) => {
    const selectedFiles = Array.from(event.target.files || [])
    if (!isEmpty(selectedFiles)) {
      setValue(controlName, [...documents, ...selectedFiles])
    }
    event.target.value = ''
  }

  const handleView = (document) => {
    const url = isFileDocument(document)
      ? URL.createObjectURL(document)
      : `${getBaseURL('dponet')}${document?.url}`
    window.open(url, '_blank')
  }

  const handleConfirmRemove = () => {
    setValue(
      controlName,
      documents.filter((document) => document !== documentToRemove),
    )
    setDocumentToRemove(null)
  }

  useEffect(() => {
    register({ name: controlName })
  }, [register, controlName])

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Label description="Documentos Anexados" marginTopChildren={0} />
        {!isShow && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlusCircleIcon size={16} />}
              onClick={handleAddClick}
            >
              Adicionar documento(s)
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={EVIDENCE_FILE_TYPES}
              hidden
              onChange={handleFilesSelected}
            />
          </>
        )}
      </Box>
      <Box className={classes.tableContainer}>
        <Table size="small" emptyMessage="Nenhum documento anexado." noWrap>
          <TableBody>
            {documents.map((document, index) => (
              <TableRow key={index}>
                <TableCell>{documentName(document)}</TableCell>
                <TableCell align="right">
                  <MenuButton>
                    <Button
                      color="secondary"
                      fullWidth
                      onClick={() => handleView(document)}
                    >
                      Visualizar
                    </Button>
                    {!isShow && (
                      <Button
                        color="secondary"
                        fullWidth
                        onClick={() => setDocumentToRemove(document)}
                      >
                        Remover
                      </Button>
                    )}
                  </MenuButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {errors?.[controlName] && (
        <FormHelperText error>{errors[controlName]?.message}</FormHelperText>
      )}

      {documentToRemove && (
        <ConfirmationDialog
          open={!!documentToRemove}
          setOpen={(value) => !value && setDocumentToRemove(null)}
          text="Tem certeza que deseja remover este documento?"
          textButton="Remover"
          actionAcceptButton={handleConfirmRemove}
        />
      )}
    </Box>
  )
}

EvidenceDocumentsTable.propTypes = {
  controlName: PropTypes.string,
  attachments: PropTypes.array,
}

export default EvidenceDocumentsTable
